import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPublicDownloads, incrementDownloadCount } from '../services/api';
import './Downloads.css';

const PLATFORM_MARKS = {
  windows: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M3 5.27 10.5 4.2v7.09H3V5.27Zm8.25-1.2L21 2.7v8.59h-9.75V4.07ZM3 12.64h7.5v7.09L3 18.66v-6.02Zm8.25 0H21v8.66l-9.75-1.37v-7.29Z" />
    </svg>
  ),
  macos: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83ZM13 3.5c.73-.83 1.22-1.98 1.08-3.13-1.05.04-2.32.7-3.08 1.58-.68.77-1.27 2-1.11 3.17 1.17.09 2.37-.6 3.11-1.62Z" />
    </svg>
  ),
  linux: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M12.5 2.2c.9 0 1.6.5 2.1 1.3.4.7.6 1.6.5 2.5-.1 1.2-.6 2.3-1.3 3.1-.2.3-.1.5.1.7 1.5 1.2 3.6 3.1 4.1 5.5.3 1.3.1 2.5-.5 3.4-.2.3 0 .6.3.8.7.5 1.5 1.4 1.5 2.5 0 .9-.8 1.5-1.8 2-1 .5-2.2.9-3 1.8-.6.7-1.4 1.5-2.7 1.5-1.1 0-1.7-.6-2.7-.6s-1.7.7-2.8.6c-1.2-.1-2.1-1-2.8-1.9-.2-.2-.5-.2-.6 0-.6.8-1.5 1.3-2.4 1.2-1-.1-1.6-.8-1.6-1.7 0-.8.6-1.6 1.1-2.1.3-.3.4-.7.2-1-.6-1-.8-2.2-.5-3.4.6-2.3 2.6-4.3 4.1-5.5.3-.2.3-.5.1-.8-.7-.9-1.4-2.6-1.2-4.3.3-2.1 2.6-2.3 4-.2.9.1 1.6.6 2.3 1 .2.2.5.2.6 0 .5-.5 1.2-.9 1.9-1 .3-.1.7-.1 1.1-.1Zm-1.1 5.1c-.5 0-.9.5-.9 1.1s.4 1.1.9 1.1.9-.5.9-1.1-.4-1.1-.9-1.1Zm3.1 0c-.5 0-.9.5-.9 1.1s.4 1.1.9 1.1.9-.5.9-1.1-.4-1.1-.9-1.1Z" />
    </svg>
  ),
  android: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M17.6 9.48l1.5-2.6a.5.5 0 10-.87-.5l-1.52 2.63A9.4 9.4 0 0012 8.2a9.4 9.4 0 00-4.71.81L5.77 6.38a.5.5 0 10-.87.5l1.5 2.6A8.4 8.4 0 003.2 15.4h17.6a8.4 8.4 0 00-3.2-5.92zM7.5 13.2a1 1 0 110 2 1 1 0 010-2zm9 0a1 1 0 110 2 1 1 0 010-2z" />
    </svg>
  ),
  ios: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83ZM13 3.5c.73-.83 1.22-1.98 1.08-3.13-1.05.04-2.32.7-3.08 1.58-.68.77-1.27 2-1.11 3.17 1.17.09 2.37-.6 3.11-1.62Z" />
    </svg>
  ),
};

const PLATFORM_TONE = {
  windows: '#0b63b8',
  macos: '#1f2937',
  linux: '#b45309',
  android: '#15803d',
  ios: '#1d4ed8',
};

const REQ_ORDER = ['os', 'cpu', 'ram', 'disk', 'network'];

const REQ_LABELS = {
  en: { os: 'OS', cpu: 'CPU', ram: 'Memory', disk: 'Disk', network: 'Network' },
  tr: { os: 'İşletim sistemi', cpu: 'İşlemci', ram: 'Bellek', disk: 'Disk', network: 'Ağ' },
  fr: { os: 'OS', cpu: 'CPU', ram: 'Mémoire', disk: 'Disque', network: 'Réseau' },
};

const CLIENT_FEATURES = {
  en: {
    windows: ['System tray controls', 'Split tunneling', 'Auto reconnect', 'Company login'],
    macos: ['Menu bar status', 'Keychain auth', 'Wake reconnect', 'Apple Silicon ready'],
    linux: ['Desktop + service mode', 'CLI helpers', 'Deb/RPM friendly', 'Headless option'],
    android: ['Always-on VPN', 'Biometric unlock', 'Quick settings tile', 'Battery aware'],
    ios: ['On Demand rules', 'Face ID unlock', 'Widget-ready', 'iPad support'],
  },
  tr: {
    windows: ['Sistem tepsisi kontrolleri', 'Split tunneling', 'Otomatik yeniden bağlanma', 'Şirket girişi'],
    macos: ['Menü çubuğu durumu', 'Keychain kimlik doğrulama', 'Uyanınca yeniden bağlanma', 'Apple Silicon'],
    linux: ['Masaüstü + servis modu', 'CLI yardımcıları', 'Deb/RPM uyumlu', 'Headless seçenek'],
    android: ['Always-on VPN', 'Biyometrik kilit açma', 'Hızlı ayarlar kutucuğu', 'Batarya dostu'],
    ios: ['On Demand kuralları', 'Face ID', 'Widget hazır', 'iPad desteği'],
  },
  fr: {
    windows: ['Contrôles barre système', 'Split tunneling', 'Reconnexion auto', 'Connexion entreprise'],
    macos: ['Statut barre de menu', 'Auth Keychain', 'Reconnexion au réveil', 'Apple Silicon'],
    linux: ['Bureau + mode service', 'Helpers CLI', 'Compatible Deb/RPM', 'Option headless'],
    android: ['VPN always-on', 'Déverrouillage biométrique', 'Tuile réglages rapides', 'Économe en batterie'],
    ios: ['Règles On Demand', 'Face ID', 'Prêt pour widgets', 'Support iPad'],
  },
};

const INSTALL_STEPS = {
  en: {
    windows: [
      'Download the installer and run as administrator if prompted.',
      'Complete setup, then open ArcVPN from the Start menu.',
      'Sign in with your company account and connect.',
    ],
    macos: [
      'Open the downloaded package and drag ArcVPN into Applications.',
      'Allow VPN configuration when macOS asks for permission.',
      'Sign in from the menu bar icon and choose a node.',
    ],
    linux: [
      'Install the package with your distro tool or AppImage.',
      'Launch ArcVPN and grant network privileges if required.',
      'Sign in and connect to an approved location.',
    ],
    android: [
      'Install from the store listing or sideload the release build.',
      'Open ArcVPN and allow VPN permission.',
      'Sign in, enable Always-on if needed, then connect.',
    ],
    ios: [
      'Install from the App Store build.',
      'Allow VPN configuration and notifications.',
      'Sign in with Face ID if enabled, then connect.',
    ],
  },
  tr: {
    windows: [
      'Kurucuyu indirin, istenirse yönetici olarak çalıştırın.',
      'Kurulumu tamamlayıp ArcVPN’i Başlat menüsünden açın.',
      'Şirket hesabınızla giriş yapın ve bağlanın.',
    ],
    macos: [
      'Paketi açıp ArcVPN’i Applications klasörüne sürükleyin.',
      'macOS VPN yapılandırması istediğinde izin verin.',
      'Menü çubuğu ikonundan giriş yapıp bir düğüm seçin.',
    ],
    linux: [
      'Paketi distro aracınızla veya AppImage olarak kurun.',
      'ArcVPN’i başlatın ve gerekirse ağ izni verin.',
      'Giriş yapıp onaylı bir lokasyona bağlanın.',
    ],
    android: [
      'Mağaza listesinden kurun veya sürümü yükleyin.',
      'ArcVPN’i açın ve VPN iznini verin.',
      'Giriş yapın, gerekirse Always-on’u açın ve bağlanın.',
    ],
    ios: [
      'App Store sürümünü kurun.',
      'VPN yapılandırması ve bildirimlere izin verin.',
      'Face ID açıksa onaylayın ve bağlanın.',
    ],
  },
  fr: {
    windows: [
      'Téléchargez l’installateur et exécutez-le en administrateur si demandé.',
      'Terminez l’installation, puis ouvrez ArcVPN depuis le menu Démarrer.',
      'Connectez-vous avec votre compte entreprise et connectez-vous.',
    ],
    macos: [
      'Ouvrez le paquet téléchargé et glissez ArcVPN dans Applications.',
      'Autorisez la configuration VPN lorsque macOS le demande.',
      'Connectez-vous depuis l’icône de la barre de menu et choisissez un nœud.',
    ],
    linux: [
      'Installez le paquet avec l’outil de votre distro ou AppImage.',
      'Lancez ArcVPN et accordez les privilèges réseau si nécessaire.',
      'Connectez-vous et rejoignez un emplacement approuvé.',
    ],
    android: [
      'Installez depuis le store ou chargez la version sideload.',
      'Ouvrez ArcVPN et autorisez la permission VPN.',
      'Connectez-vous, activez Always-on si besoin, puis connectez.',
    ],
    ios: [
      'Installez depuis l’App Store.',
      'Autorisez la configuration VPN et les notifications.',
      'Validez Face ID si activé, puis connectez-vous.',
    ],
  },
};

const resolvePlatformKey = (download) => {
  const icon = String(download?.platform_icon || '').toLowerCase();
  const platform = String(download?.platform || '').toLowerCase();
  const name = String(download?.platform_name || '').toLowerCase();
  if (icon.includes('windows') || platform.includes('windows') || name.includes('windows')) return 'windows';
  if (icon.includes('mac') || platform.includes('mac') || name.includes('mac')) return 'macos';
  if (icon.includes('linux') || platform.includes('linux') || name.includes('linux')) return 'linux';
  if (icon.includes('android') || platform.includes('android') || name.includes('android')) return 'android';
  if (icon.includes('ios') || platform.includes('ios') || name.includes('ios') || name.includes('iphone')) return 'ios';
  return platform || 'windows';
};

const parseRequirements = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const buildRequirementRows = (reqs, minRequirements, lang) => {
  const labels = REQ_LABELS[lang] || REQ_LABELS.en;
  if (reqs && typeof reqs === 'object') {
    const keys = [
      ...REQ_ORDER.filter((key) => reqs[key]),
      ...Object.keys(reqs).filter((key) => !REQ_ORDER.includes(key)),
    ];
    return keys.map((key) => ({
      key,
      label: labels[key] || key.replace(/_/g, ' '),
      value: String(reqs[key]),
    }));
  }
  if (minRequirements) {
    return [{ key: 'os', label: labels.os, value: minRequirements }];
  }
  return [];
};

const resolveLangPack = (language) => {
  const lang = String(language || '').toLowerCase();
  if (lang.startsWith('tr')) return 'tr';
  if (lang.startsWith('fr')) return 'fr';
  return 'en';
};

const Downloads = () => {
  const { t, i18n } = useTranslation();
  const langPack = resolveLangPack(i18n.language);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);

  useEffect(() => {
    fetchDownloads();
  }, []);

  const fetchDownloads = async () => {
    try {
      const data = await getPublicDownloads();
      const list = data || [];
      setDownloads(list);
      if (list.length) setSelectedId(list[0].id);
    } catch (err) {
      console.error('Failed to fetch downloads:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (filter === 'all') return downloads;
    if (filter === 'desktop') {
      return downloads.filter((d) => ['windows', 'macos', 'linux'].includes(resolvePlatformKey(d)));
    }
    if (filter === 'mobile') {
      return downloads.filter((d) => ['android', 'ios'].includes(resolvePlatformKey(d)));
    }
    return downloads;
  }, [downloads, filter]);

  const selected = useMemo(
    () => filtered.find((d) => d.id === selectedId) || filtered[0] || null,
    [filtered, selectedId]
  );

  useEffect(() => {
    if (filtered.length && !filtered.some((d) => d.id === selectedId)) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  const handleDownload = async (download) => {
    if (!download?.download_url || download.download_url === '#' || download.download_url === '') {
      setShowUnavailableModal(true);
      return;
    }
    try {
      await incrementDownloadCount(download.id);
    } catch (err) {
      console.error('Failed to increment download count:', err);
    }
    window.open(download.download_url, '_blank');
  };

  const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="dl-page">
        <div className="dl-empty">
          <div className="dl-spinner" />
          <p>{t('common.loading', 'Loading…')}</p>
        </div>
      </div>
    );
  }

  const selectedKey = selected ? resolvePlatformKey(selected) : null;
  const reqs = selected ? parseRequirements(selected.system_requirements) : null;
  const features = selectedKey ? CLIENT_FEATURES[langPack][selectedKey] || [] : [];
  const steps = selectedKey ? INSTALL_STEPS[langPack][selectedKey] || [] : [];
  const requirementRows = selected
    ? buildRequirementRows(reqs, selected.min_requirements, langPack)
    : [];

  return (
    <div className="dl-page">
      <section className="dl-hero">
        <div className="dl-hero-copy">
          <p className="dl-kicker">{t('downloads.kicker', 'Clients')}</p>
          <h1>{t('downloads.title', 'ArcVPN clients')}</h1>
          <p className="dl-lead">
            {t(
              'downloads.subtitle',
              'Polished apps for every platform your team uses — with clear requirements and release details.'
            )}
          </p>
          <p className="dl-lead-mobile">
            {t('downloads.leadMobile', 'Choose a platform, then download.')}
          </p>
        </div>
        <div className="dl-hero-meta">
          <div>
            <strong>{downloads.length}</strong>
            <span>{t('downloads.platforms', 'Platforms')}</span>
          </div>
          <div>
            <strong>{downloads[0]?.version || 'v1.0.0'}</strong>
            <span>{t('downloads.latest', 'Latest line')}</span>
          </div>
        </div>
      </section>

      <div className="dl-toolbar">
        <div className="dl-filters" role="tablist">
          {[
            { key: 'all', label: t('downloads.filter.all', 'All') },
            { key: 'desktop', label: t('downloads.filter.desktop', 'Desktop') },
            { key: 'mobile', label: t('downloads.filter.mobile', 'Mobile') },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              className={`dl-filter ${filter === item.key ? 'is-active' : ''}`}
              onClick={() => setFilter(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="dl-empty soft">
          <h3>{t('downloads.noDownloads', 'No downloads available yet')}</h3>
          <p>{t('downloads.noDownloadsDesc', 'Please check back shortly.')}</p>
        </div>
      ) : (
        <>
          <section
            className="dl-rail"
            style={{ '--cols': Math.max(filtered.length, 1) }}
          >
            {filtered.map((download) => {
              const key = resolvePlatformKey(download);
              const tone = PLATFORM_TONE[key] || '#12786f';
              const active = selected?.id === download.id;
              return (
                <button
                  key={download.id}
                  type="button"
                  className={`dl-platform ${active ? 'is-active' : ''}`}
                  style={{ '--tone': tone }}
                  onClick={() => setSelectedId(download.id)}
                >
                  <span className="dl-mark">{PLATFORM_MARKS[key] || PLATFORM_MARKS.windows}</span>
                  <span className="dl-platform-copy">
                    <strong>{download.platform_name}</strong>
                    <em>
                      {download.version} · {download.file_size}
                    </em>
                  </span>
                </button>
              );
            })}
          </section>

          {selected && (
            <article className="dl-detail" style={{ '--tone': PLATFORM_TONE[selectedKey] || '#12786f' }}>
              <header className="dl-detail-head">
                <div className="dl-detail-brand">
                  <span className="dl-mark xl">{PLATFORM_MARKS[selectedKey] || PLATFORM_MARKS.windows}</span>
                  <div>
                    <p className="dl-kicker">{t('downloads.client', 'Client')}</p>
                    <h2>{selected.platform_name}</h2>
                    <p className="dl-detail-desc">
                      {selected.description ||
                        t(
                          'downloads.defaultDesc',
                          'Secure ArcVPN client for your organization.'
                        )}
                    </p>
                  </div>
                </div>
                <button type="button" className="dl-btn primary" onClick={() => handleDownload(selected)}>
                  {selected.download_type === 'store'
                    ? t('downloads.openStore', 'Open store')
                    : t('downloads.download', 'Download')}
                </button>
              </header>

              <div className="dl-specs">
                <div>
                  <span>{t('downloads.version', 'Version')}</span>
                  <strong>{selected.version}</strong>
                </div>
                <div>
                  <span>{t('downloads.size', 'Size')}</span>
                  <strong>{selected.file_size}</strong>
                </div>
                <div>
                  <span>{t('downloads.arch', 'Architectures')}</span>
                  <strong>{selected.architectures || '—'}</strong>
                </div>
                <div>
                  <span>{t('downloads.updated', 'Updated')}</span>
                  <strong>{formatDate(selected.updated_at)}</strong>
                </div>
                <div>
                  <span>{t('downloads.minOs', 'Minimum OS')}</span>
                  <strong>{selected.min_requirements || '—'}</strong>
                </div>
                <div>
                  <span>{t('downloads.channel', 'Channel')}</span>
                  <strong>
                    {selected.download_type === 'store'
                      ? t('downloads.type.store', 'Store')
                      : t('downloads.type.link', 'Direct link')}
                  </strong>
                </div>
              </div>

              <section className="dl-req-panel">
                <div className="dl-section-head">
                  <h3>{t('downloads.requirements', 'System requirements')}</h3>
                  <p>{selected.min_requirements || t('downloads.reqHint', 'Hardware and OS baseline for this client.')}</p>
                </div>
                {requirementRows.length > 0 ? (
                  <div className="dl-req-grid">
                    {requirementRows.map((row) => (
                      <div key={row.key} className="dl-req-card">
                        <span>{row.label}</span>
                        <strong>{row.value}</strong>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="dl-muted">{t('downloads.noReqs', 'No detailed requirements published yet.')}</p>
                )}
              </section>

              {selected.checksum_sha256 ? (
                <div className="dl-checksum">
                  <span>SHA-256</span>
                  <code>{selected.checksum_sha256}</code>
                </div>
              ) : null}

              <div className="dl-columns">
                <section>
                  <h3>{t('downloads.features', 'Client features')}</h3>
                  <ul className="dl-list">
                    {features.map((feature) => (
                      <li key={feature}>
                        <span>✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </section>
                <section className="dl-install">
                  <h3>{t('downloads.install', 'Install')}</h3>
                  <ol>
                    {steps.map((step, index) => (
                      <li key={step}>
                        <em>{index + 1}</em>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </section>
              </div>

              {selected.changelog && (
                <section className="dl-changelog">
                  <h3>{t('downloads.changelog', 'Release notes')}</h3>
                  <p>{selected.changelog}</p>
                </section>
              )}
            </article>
          )}
        </>
      )}

      {showUnavailableModal && (
        <div className="dl-modal-overlay" onClick={() => setShowUnavailableModal(false)}>
          <div className="dl-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('downloads.unavailable.title', 'Client being updated')}</h3>
            <p>
              {t(
                'downloads.unavailable.message',
                'This build is not published yet. Please try again shortly.'
              )}
            </p>
            <button type="button" className="dl-btn primary" onClick={() => setShowUnavailableModal(false)}>
              {t('common.ok', 'OK')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Downloads;
